// #4008 FWF — CANVAS IDENTITY / ANSWER AUTHORITY. The closure of the flagship well.
//
// THE DEFECT THIS CLOSES (diagnosed in `.codex/reports/4008-fwd/`). The composite
// operating-decision-package canvas prints an OBJECTIVE block, a REQUIRED UPLIFT
// and a VERDICT for ONE governed objective — finance operating income. Admission
// to that canvas was decided by the turn's `intentClass` alone, and
// `intentClass: 'value_gap_bridge'` is a class that 24 module intents across 18
// decision modules are entitled to declare. Four captured rows from four
// different modules therefore minted the SAME canvas, the same specId, the same
// baseline and the same verdict, for four different questions — while the body of
// each canvas carried the engine's own honest sentence saying the asked question
// could not be answered from the record.
//
// THE RULE. Printing an objective's headline is an AUTHORITY, not a capability.
// A canvas may claim the objective block only when the intent the planner actually
// selected is declared by the module that OWNS that objective. Everyone else may
// contribute to the bridge; nobody else may become it.
//
// WHY THIS FILE EXISTS IN packages/lib. The rule is asked in two places that must
// never drift apart: the runtime admission seam (`arbitrateValueGapBridgeAdmission`
// in ask-helix.query-runtime.service.ts, which refuses to COMPOSE the composite)
// and the canvas seam (`ask-helix.decision-agent-view-spec.ts`, which refuses to
// PRINT the objective block). One predicate, one owner, asked twice. Closed enum:
// model proposes, code disposes.
//
// WHAT THIS FILE IS NOT. It is not a metric table and it carries no company data.
// `OBJECTIVE_OWNER_CAPABILITY_ID` de-duplicates a literal the runtime already
// hardcoded in three places (`FINANCE_VALUE_GAP_CAPABILITY_ID`); it is a
// substrate fact about which capability produces the governed value-gap anchor,
// not a fact about any tenant, industry, or dataset. A data swap changes nothing
// here.

/**
 * The capability that OWNS the governed objective value-gap anchor, and is
 * therefore the only declarer entitled to mint the objective canvas.
 *
 * This is the same identity `hasPlannedFinanceValueGapAnchor` already requires of
 * the planned anchor call; naming it once keeps the admission seam and the canvas
 * seam from drifting.
 */
export const OBJECTIVE_OWNER_CAPABILITY_ID = 'finance';

/**
 * The narration module id the composite bridge publishes under. The composite is
 * the objective owner's OWN composite surface — it is a legitimate identity for
 * the objective canvas, and never an identity a contributing module may borrow.
 */
export const OBJECTIVE_COMPOSITE_MODULE_ID = 'operating-decision-package';

/** The turn identity a canvas must carry before it may claim an objective. */
export type AskedTurnIdentity = {
  /** The intent the planner actually selected. Never derived from the question. */
  askedIntentId: string | null | undefined;
  /** The module that DECLARES askedIntentId — never the narration moduleId. */
  askedModuleId: string | null | undefined;
};

function normalized(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Does this asked identity own the objective canvas?
 *
 * True only for the objective owner's own declarations and for the owner's
 * composite surface. Every contributing module — the 24 `*_size_contribution` /
 * `*_contribution_bridge` doors — answers false, which is the whole point.
 */
export function askedIdentityOwnsObjectiveCanvas(
  identity: AskedTurnIdentity,
): boolean {
  const askedModuleId = normalized(identity.askedModuleId);
  if (!askedModuleId) return false;
  return (
    askedModuleId === OBJECTIVE_OWNER_CAPABILITY_ID ||
    askedModuleId === OBJECTIVE_COMPOSITE_MODULE_ID
  );
}

/** The named reasons a canvas may be refused objective authority. */
export type ObjectiveCanvasAuthorityViolation =
  /** The turn carries no asked identity at all — fail closed, never guess. */
  | 'asked_identity_absent'
  /** A module that does not own the objective tried to print its headline. */
  | 'asked_module_not_objective_owner';

/**
 * The fail-closed assertion, asked of any canvas about to print an objective
 * block. Returns the violation, or null when the canvas may proceed.
 *
 * `claimsObjective` is the caller's own answer to "is this canvas about to print
 * OBJECTIVE / REQUIRED UPLIFT / VERDICT for a governed objective?". When it is
 * false the assertion is silent: module canvases that never claim an objective
 * are entirely out of scope and must not be disturbed.
 *
 * Behaviour on violation is the CALLER's, and it is always the same shape:
 * refuse, never invent. The admission seam declines to compose the composite so
 * the asked module answers in its own lane; the canvas seam demotes the objective
 * from headline to context. Neither adds, relaxes, or synthesizes a number — the
 * anti-fabrication fence is untouched by this file.
 */
export function objectiveCanvasAuthorityViolation(input: {
  claimsObjective: boolean;
  identity: AskedTurnIdentity;
}): ObjectiveCanvasAuthorityViolation | null {
  if (!input.claimsObjective) return null;
  if (
    !normalized(input.identity.askedIntentId) &&
    !normalized(input.identity.askedModuleId)
  ) {
    return 'asked_identity_absent';
  }
  return askedIdentityOwnsObjectiveCanvas(input.identity)
    ? null
    : 'asked_module_not_objective_owner';
}
